export type WsFilterOperator =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'contains'
  | 'in'
  | 'isnull'
  | 'notnull';

export type WsFilterPrimitive = string | number | boolean | null;

export type WsFilterCondition = {
  field: string;
  op: WsFilterOperator;
  value?: WsFilterPrimitive | WsFilterPrimitive[];
};

export const parseSearchInputToFilters = (
  input: string,
): { search: string; filters: WsFilterCondition[] } => {
  const normalized = input.trim();
  if (!normalized) {
    return { search: '', filters: [] };
  }

  const normalizedWithoutWhere = normalized.replace(/^where\s+/i, '').trim();
  if (!normalizedWithoutWhere || !normalizedWithoutWhere.includes('=')) {
    return { search: normalizedWithoutWhere || normalized, filters: [] };
  }

  const chunks = normalizedWithoutWhere
    .split(/\s+and\s+/i)
    .map(chunk => chunk.trim())
    .filter(Boolean);
  if (!chunks.length) {
    return { search: normalizedWithoutWhere, filters: [] };
  }

  const filters: WsFilterCondition[] = [];
  for (const chunk of chunks) {
    const match = chunk.match(/^"?([^"=<>!~]+?)"?\s*(=|!=|>=|<=|>|<|~)\s*(.+)$/);
    if (!match) {
      return { search: normalizedWithoutWhere, filters: [] };
    }

    const [, rawField, rawOperator, rawValue] = match;
    const field = rawField.trim();
    if (!field) {
      return { search: normalizedWithoutWhere, filters: [] };
    }

    const unwrappedValue = rawValue.trim().replace(/^['"]|['"]$/g, '');
    let value: string | number | boolean = unwrappedValue;
    if (/^(true|false)$/i.test(unwrappedValue)) {
      value = /^true$/i.test(unwrappedValue);
    } else if (/^-?\d+(?:\.\d+)?$/.test(unwrappedValue)) {
      value = Number(unwrappedValue);
    }

    const operatorMap: Record<string, WsFilterOperator> = {
      '=': 'eq',
      '!=': 'neq',
      '>': 'gt',
      '>=': 'gte',
      '<': 'lt',
      '<=': 'lte',
      '~': 'contains',
    };
    const op = operatorMap[rawOperator];
    if (!op) {
      return { search: normalizedWithoutWhere, filters: [] };
    }

    filters.push({ field, op, value });
  }

  if (!filters.length) {
    return { search: normalizedWithoutWhere, filters: [] };
  }

  return { search: '', filters };
};
